/**
 * InstagramHealth.jsx
 * Every brand's Instagram account, as Meta sees it, side by side.
 *
 * Exists to answer "why does this account's every post show zero?" — which
 * Kreeboom's own records cannot, because they only hold what Meta returned.
 * The accounts are shown next to each other deliberately: the explanation is
 * nearly always in the difference between one that works and one that does
 * not.
 *
 * Read-only.
 *
 * Dependencies: services/api.js, helpers.js, components/Icons.jsx
 */

import { useState } from 'react';
import { api } from '../../services/api.js';
import { Refresh, Warning } from '../Icons.jsx';
import { compact, snippet } from './helpers.js';

export default function InstagramHealth() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      setData(await api.analytics.instagramHealth());
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div style={{ marginTop: 14 }}>
        <button className="btn btn--sm" onClick={run} disabled={busy}>
          <Refresh /> {busy ? 'Asking Meta…' : 'Check Instagram accounts with Meta'}
        </button>
        {error && (
          <div className="notice notice--error" style={{ marginTop: 10 }}>
            <Warning />
            <span>{error.message}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ig-health">
      <div className="ig-health__grid">
        {data.accounts.map((acc) => (
          <Account key={acc.igUserId ?? acc.brand} acc={acc} />
        ))}
      </div>

      <div className="btn-row" style={{ marginTop: 10 }}>
        <button className="btn btn--sm" onClick={run} disabled={busy}>
          <Refresh /> {busy ? 'Asking Meta…' : 'Check again'}
        </button>
        <button className="btn btn--sm" onClick={() => setData(null)}>
          Close
        </button>
      </div>
    </div>
  );
}

function Account({ acc }) {
  if (acc.problem) {
    return (
      <div className="ig-health__card">
        <div className="ig-health__brand">{acc.brand}</div>
        <p className="hint hint--error">{acc.problem}</p>
      </div>
    );
  }

  const p = acc.profile ?? {};
  const r = acc.reach28 ?? {};
  const posts = Array.isArray(acc.posts) ? acc.posts : [];

  return (
    <div className="ig-health__card">
      <div className="ig-health__brand">
        {acc.brand} {p.username && <span className="ig-health__user">@{p.username}</span>}
      </div>

      {p.error ? (
        <p className="hint hint--error">Profile: {p.error}</p>
      ) : (
        <div className="ig-health__figures">
          <Fig value={compact(p.followers)} label="Followers" />
          <Fig value={compact(p.following)} label="Following" />
          <Fig value={compact(p.mediaCount)} label="Posts on Instagram" />
          <Fig value={compact(acc.publishedByKreeboom)} label="Posted by Kreeboom" />
        </div>
      )}

      <div className="ig-health__label">Whole account, last 28 days</div>
      {r.error ? (
        <p className="hint hint--error">{r.error}</p>
      ) : (
        <div className="ig-health__figures">
          <Fig value={compact(r.reach)} label="Accounts reached" />
          <Fig value={compact(r.views)} label="Views" />
          <Fig value={compact(r.accountsEngaged)} label="Accounts engaged" />
        </div>
      )}

      <div className="ig-health__label">Latest posts, read fresh from Meta</div>
      {acc.posts?.error ? (
        <p className="hint hint--error">{acc.posts.error}</p>
      ) : posts.length === 0 ? (
        <p className="hint">Meta lists no posts on this account.</p>
      ) : (
        <ul className="ig-health__posts">
          {posts.map((post) => (
            <li key={post.id}>
              <span className="ig-health__caption">
                {post.permalink ? (
                  <a href={post.permalink} target="_blank" rel="noopener noreferrer">
                    {snippet(post.caption, 44) || 'No caption'}
                  </a>
                ) : (
                  snippet(post.caption, 44) || 'No caption'
                )}
              </span>
              <span className="ig-health__nums">
                {compact(post.views)} views · {compact(post.reach)} reached · {compact(post.likes)}{' '}
                likes · {compact(post.comments)} comments
              </span>
              {post.insightsError && <span className="hint hint--error">{post.insightsError}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const Fig = ({ value, label }) => (
  <div className="ig-health__fig">
    <strong>{value}</strong>
    <small>{label}</small>
  </div>
);
